/**
 * Session Results Page
 *
 * Organises completed sessions by Cycle → Track.
 * For each session shows:
 *  - Summary stats (items ranked, tool used, participants)
 *  - Normalised-score bar chart (top-N items)
 *  - Tier distribution donut chart
 *  - Ranked item list with links to item detail
 *
 * Relies only on data already in the Zustand stores — no extra fetches.
 */

import { ObjectRef } from '@/components/ds/ObjectRef';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AgentBar } from '../../../components/agents/AgentBar';
import { twinsAgent } from '../../../components/agents/contextualAgents';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from 'recharts';
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Target,
  Users,
  BarChart2,
  Trophy,
  Clock,
  CheckCircle2,
  AlertCircle,
  Search,
  X,
  Filter,
} from 'lucide-react';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useTrackStore } from '../../../stores/prioritization/trackStore';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { MatrixToolResultsCharts } from '../../../components/prioritization/reports/MatrixToolResultsCharts';
import {
  resolveMatrixReportVariant,
  resolveItemBucket,
  formatBucketLabel,
  quadrantMetaByKey,
} from '../../../lib/sessionResultVisualizations';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { SESSION_STATE_LABELS } from '@/lib/sessions/vocabulary';
import { itemInContextHref } from '@/lib/itemCycleStanding';

// ── colour helpers ────────────────────────────────────────────────────────────

const TIER_COLORS: Record<string, string> = {
  high:     '#22c55e',
  medium:   '#f59e0b',
  low:      '#ef4444',
  top:      '#6366f1',
  bottom:   '#94a3b8',
  include:  '#22c55e',
  exclude:  '#94a3b8',
};
function tierColor(tier: string) {
  return TIER_COLORS[tier?.toLowerCase()] ?? '#6366f1';
}

const STATE_META: Record<string, { label: string; bg: string; text: string }> = {
  PUBLISHED:       { label: SESSION_STATE_LABELS.PUBLISHED, bg: 'bg-[var(--green-dim)]',  text: 'text-[var(--green)] dark:text-[var(--text-ter)]'  },
  RUN_COMPLETE:    { label: SESSION_STATE_LABELS.RUN_COMPLETE, bg: 'bg-[var(--slate-dim)]',   text: 'text-[var(--text-sec)]'   },
  CRITERIA_FINALIZED: { label: SESSION_STATE_LABELS.CRITERIA_FINALIZED, bg: 'bg-[var(--mustard-dim)]', text: 'text-[var(--mustard)]' },
  READY_TO_RUN:    { label: 'Ready to Run',  bg: 'bg-[var(--mustard-dim)]',  text: 'text-[var(--mustard)]'  },
  SCORING:         { label: 'Scoring',        bg: 'bg-[var(--slate-dim)]', text: 'text-[var(--text-sec)]' },
  CRITERIA_LOCKED: { label: 'Criteria Set',  bg: 'bg-[var(--slate-dim)]', text: 'text-[var(--text-sec)]' },
  DRAFT:           { label: SESSION_STATE_LABELS.DRAFT, bg: 'bg-[var(--hover-tint)]',   text: 'text-[var(--text-sec)]'   },
  ARCHIVED:        { label: 'Archived',       bg: 'bg-[var(--hover-tint)]',   text: 'text-[var(--text-ter)]'   },
};
function stateBadge(state: string) {
  const m = STATE_META[state] ?? { label: state, bg: 'bg-[var(--hover-tint)]', text: 'text-[var(--text-sec)]' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${m.bg} ${m.text}`}>
      {m.label}
    </span>
  );
}

function formatDate(d: Date | string | undefined) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── SessionCard ───────────────────────────────────────────────────────────────

function SessionCard({ session, itemsMap }: {
  session: PrioritizationSession;
  itemsMap: Map<string, { title: string; itemCategory?: string }>;
}) {
  const [expanded, setExpanded] = useState(false);
  const output = session.rankingOutput;
  const rankedIds: string[] = output?.rankedItemIds ?? [];
  const tiers: Record<string, string> = output?.tiers ?? {};
  const hasResults = rankedIds.length > 0;
  const matrixVariant = resolveMatrixReportVariant(output?.toolId ?? session.selectedToolId);

  // For V3 criteria sessions the ranked IDs are crit_xxxxx, not workspace UUIDs.
  // Build a supplemental name map from v3Config.criteria so bars show real names.
  const criteriaNameMap = useMemo(() => {
    const v3Criteria = (session.modeConfig as Record<string, unknown>)
      ?.v3Config as { criteria?: Array<{ id: string; name: string }> } | undefined;
    const m = new Map<string, string>();
    v3Criteria?.criteria?.forEach(c => m.set(c.id, c.name));
    return m;
  }, [session.modeConfig]);

  const resolveItemTitle = (id: string) =>
    itemsMap.get(id)?.title ?? criteriaNameMap.get(id) ?? id;

  // Compute display scores: prefer normalizedScores if populated, otherwise sum
  // per-attribute raw scores, otherwise fall back to rank-based positioning.
  const displayScores = useMemo<Record<string, number>>(() => {
    const norm = output?.normalizedScores ?? {};
    if (Object.keys(norm).length > 0) return norm;

    // Sum per-attribute scores for each item
    const raw = output?.scores ?? {};
    const totals: Record<string, number> = {};
    for (const id of rankedIds) {
      const attrs = raw[id] ?? {};
      totals[id] = Object.values(attrs).reduce((s: number, v) => s + (typeof v === 'number' ? v : 0), 0);
    }
    const maxTotal = Math.max(...Object.values(totals), 1);
    if (maxTotal > 0) {
      // Normalise to 0–100
      const scaled: Record<string, number> = {};
      for (const id of rankedIds) scaled[id] = Math.round(((totals[id] ?? 0) / maxTotal) * 100);
      return scaled;
    }

    // Nothing numeric — use rank position as score (1st = 100, last proportional)
    const rankBased: Record<string, number> = {};
    const total = rankedIds.length;
    rankedIds.forEach((id, i) => {
      rankBased[id] = Math.round(((total - i) / total) * 100);
    });
    return rankBased;
  }, [output, rankedIds]);

  // Bar chart data — top 10
  const barData = useMemo(() => {
    return rankedIds.slice(0, 10).map((id, i) => {
      const title = resolveItemTitle(id);
      return {
        name: title.slice(0, 28) + (title.length > 28 ? '…' : ''),
        score: displayScores[id] ?? 0,
        tier: tiers[id] ?? '',
        rank: i + 1,
        id,
      };
    });
  }, [rankedIds, displayScores, tiers, itemsMap, criteriaNameMap]);

  // Donut data — tier distribution
  const donutData = useMemo(() => {
    const counts: Record<string, number> = {};
    rankedIds.forEach(id => {
      const t = tiers[id] ?? 'untiered';
      counts[t] = (counts[t] ?? 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [rankedIds, tiers]);

  const participantCount = (session as any).participants?.length ?? (session as any).participantIds?.length ?? 0;

  return (
    <div className="border border-[var(--rule)] overflow-hidden bg-[var(--bg-raised)]">
      {/* Session header row */}
      <button
        type="button"
        onClick={() => setExpanded(p => !p)}
        className="w-full flex items-start gap-3 px-5 py-4 hover:bg-[var(--surface)] transition-colors text-left"
      >
        <span className="mt-0.5 shrink-0 text-[var(--text-ter)]">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold"><ObjectRef kind="session" name={session.name || 'Unnamed session'} /></span>
            {stateBadge(session.state)}
          </div>
          {session.description && (
            <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">{session.description}</p>
          )}
        </div>

        {/* Quick stats */}
        <div className="shrink-0 flex items-center gap-4 text-xs text-[var(--text-ter)]">
          {output?.toolName && (
            <span className="flex items-center gap-1"><BarChart2 className="h-3.5 w-3.5" />{output.toolName}</span>
          )}
          {rankedIds.length > 0 && (
            <span className="flex items-center gap-1"><Trophy className="h-3.5 w-3.5" />{rankedIds.length} items</span>
          )}
          {participantCount > 0 && (
            <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{participantCount}</span>
          )}
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{formatDate(session.updatedAt ?? session.createdAt)}</span>
        </div>

        <Link
          to={`/prioritization/reports/session/${session.id}`}
          onClick={e => e.stopPropagation()}
          className="shrink-0 ml-2 p-1 text-[var(--text-ter)] hover:text-primary-600 transition-colors"
          title="View results"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-[var(--rule)]">
          {!hasResults ? (
            <div className="px-5 py-8 text-center">
              <AlertCircle className="h-6 w-6 text-[var(--text-ter)] mx-auto mb-2" />
              <p className="text-sm text-[var(--text-ter)]">No ranking results yet for this session.</p>
            </div>
          ) : (
            <div className="p-5 space-y-6">
              {/* Charts row — matrix tools get quadrant views; others keep score bars */}
              {matrixVariant ? (
                <MatrixToolResultsCharts
                  variant={matrixVariant}
                  rankedIds={rankedIds}
                  output={output}
                  itemsMap={itemsMap}
                />
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-3">
                      Top {barData.length} by Score
                    </p>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={barData} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 8 }}>
                        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                        <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                        <Tooltip
                          formatter={(v: number) => [`${v}`, 'Score']}
                          contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                        />
                        <Bar dataKey="score" radius={[0, 4, 4, 0]} maxBarSize={16}>
                          {barData.map((entry) => (
                            <Cell key={entry.id} fill={tierColor(entry.tier)} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {donutData.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-3">
                        Tier Distribution
                      </p>
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie
                            data={donutData}
                            cx="50%"
                            cy="45%"
                            innerRadius={55}
                            outerRadius={80}
                            dataKey="value"
                            paddingAngle={2}
                          >
                            {donutData.map((entry, i) => (
                              <Cell key={i} fill={tierColor(entry.name)} />
                            ))}
                          </Pie>
                          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              )}

              {/* Full ranked list */}
              <div>
                <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-2">
                  All Ranked Items ({rankedIds.length})
                </p>
                <div className="border border-[var(--rule)] divide-y divide-[var(--rule)] overflow-hidden">
                  {rankedIds.map((id, idx) => {
                    const itemTitle = resolveItemTitle(id);
                    const item = itemsMap.get(id);
                    const score = displayScores[id];
                    const bucket = resolveItemBucket(output, id) ?? tiers[id];
                    const bucketMeta = matrixVariant && bucket
                      ? quadrantMetaByKey(matrixVariant, bucket)
                      : undefined;
                    return (
                      <div key={id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--surface)] transition-colors">
                        <span className="w-7 text-right text-xs font-mono text-[var(--text-ter)] shrink-0">{idx + 1}</span>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-[var(--text-pri)] truncate block">
                            {itemTitle}
                          </span>
                          {item?.itemCategory && (
                            <span className="text-xs text-[var(--text-ter)]">{item.itemCategory}</span>
                          )}
                        </div>
                        {bucketMeta ? (
                          <span
                            className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full border ${bucketMeta.borderClass} ${bucketMeta.textClass} ${bucketMeta.bgClass}`}
                          >
                            {bucketMeta.label}
                          </span>
                        ) : bucket && (
                          <span
                            className="shrink-0 text-xs font-medium px-2 py-0.5 rounded-full"
                            style={{ background: tierColor(bucket) + '22', color: tierColor(bucket) }}
                          >
                            {matrixVariant ? formatBucketLabel(matrixVariant, bucket) : bucket}
                          </span>
                        )}
                        {!matrixVariant && score != null && (
                          <span className="shrink-0 w-14 text-right text-xs tabular-nums text-[var(--text-ter)]">
                            {Math.round(score * 10) / 10}
                          </span>
                        )}
                        <Link
                          to={itemInContextHref(id, { cycleId: session.cycleId })}
                          className="shrink-0 p-1 text-[var(--text-ter)] hover:text-primary-600 transition-colors"
                          title="View item"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── TrackGroup ────────────────────────────────────────────────────────────────

function TrackGroup({ trackName, sessions, itemsMap }: {
  trackName: string;
  sessions: PrioritizationSession[];
  itemsMap: Map<string, { title: string; itemCategory?: string }>;
}) {
  const [open, setOpen] = useState(true);
  const completed = sessions.filter(s => ['RUN_COMPLETE', 'PUBLISHED'].includes(s.state)).length;

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[var(--surface)] transition-colors text-left group"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" /> : <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)]" />}
        <span className="text-sm font-medium"><ObjectRef kind="track" name={trackName} /></span>
        <span className="ml-auto text-xs text-[var(--text-ter)]">{sessions.length} session{sessions.length !== 1 ? 's' : ''} · {completed} complete</span>
      </button>
      {open && (
        <div className="ml-6 space-y-3">
          {sessions.map(s => (
            <SessionCard key={s.id} session={s} itemsMap={itemsMap} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── CycleGroup ────────────────────────────────────────────────────────────────

function CycleGroup({ cycleName, sessionsByTrack, itemsMap }: {
  cycleName: string;
  sessionsByTrack: Map<string, { trackName: string; sessions: PrioritizationSession[] }>;
  itemsMap: Map<string, { title: string; itemCategory?: string }>;
}) {
  const [open, setOpen] = useState(true);
  const totalSessions = [...sessionsByTrack.values()].reduce((n, t) => n + t.sessions.length, 0);
  const totalComplete = [...sessionsByTrack.values()]
    .flatMap(t => t.sessions)
    .filter(s => ['RUN_COMPLETE', 'PUBLISHED'].includes(s.state)).length;

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-[var(--surface)] transition-colors text-left"
      >
        {open ? <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" /> : <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />}
        <span className="text-sm font-bold"><ObjectRef kind="cycle" name={cycleName} /></span>
        <div className="ml-auto flex items-center gap-4 text-xs text-[var(--text-ter)]">
          <span>{totalSessions} session{totalSessions !== 1 ? 's' : ''}</span>
          <span className="flex items-center gap-1 text-[var(--green)] dark:text-[var(--text-ter)]">
            <CheckCircle2 className="h-3 w-3" />
            {totalComplete} complete
          </span>
        </div>
      </button>

      {open && (
        <div className="border-t border-[var(--rule)] px-5 py-4 space-y-4">
          {[...sessionsByTrack.entries()].map(([trackId, { trackName, sessions }]) => (
            <TrackGroup
              key={trackId}
              trackName={trackName}
              sessions={sessions}
              itemsMap={itemsMap}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function SessionReports() {
  const navigate = useNavigate();
  const pageAgents = useMemo(() => [twinsAgent(navigate)], [navigate]);
  const sessions  = useSessionStore(s => s.sessions);
  const cycles    = useDegapProcessStore(s => s.cycles);
  const tracks    = useTrackStore(s => s.tracks);
  const items     = useItemsStore(s => s.items);

  const [search, setSearch]         = useState('');
  const [stateFilter, setStateFilter] = useState<'all' | 'complete' | 'in-progress'>('all');

  // Build a fast id→item lookup
  const itemsMap = useMemo(() => {
    const m = new Map<string, { title: string; itemCategory?: string }>();
    items.forEach(i => m.set(i.id, { title: i.title, itemCategory: (i as any).itemCategory }));
    return m;
  }, [items]);

  // Build cycle id→name and track id→name lookups
  const cycleMap = useMemo(() => {
    const m = new Map<string, string>();
    cycles.forEach(c => m.set(c.id, c.name));
    return m;
  }, [cycles]);

  const trackMap = useMemo(() => {
    const m = new Map<string, string>();
    tracks.forEach(t => m.set(t.id, t.name ?? 'Unnamed Track'));
    return m;
  }, [tracks]);

  // Filter sessions
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sessions.filter(s => {
      if (stateFilter === 'complete' && !['RUN_COMPLETE', 'PUBLISHED'].includes(s.state)) return false;
      if (stateFilter === 'in-progress' && ['DRAFT', 'RUN_COMPLETE', 'PUBLISHED', 'ARCHIVED'].includes(s.state)) return false;
      if (q && !(s.name ?? '').toLowerCase().includes(q) && !(cycleMap.get(s.cycleId ?? '') ?? '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [sessions, stateFilter, search, cycleMap]);

  // Group: cycle → track → sessions[]
  const grouped = useMemo(() => {
    const byCycle = new Map<string, Map<string, PrioritizationSession[]>>();

    filtered.forEach(s => {
      const cycleKey = s.cycleId ?? '__no_cycle__';
      if (!byCycle.has(cycleKey)) byCycle.set(cycleKey, new Map());
      const trackKey = s.trackId ?? '__no_track__';
      const byTrack = byCycle.get(cycleKey)!;
      if (!byTrack.has(trackKey)) byTrack.set(trackKey, []);
      byTrack.get(trackKey)!.push(s);
    });

    // Sort sessions within each track by date desc
    byCycle.forEach(byTrack => {
      byTrack.forEach(arr => arr.sort((a, b) => {
        const ta = (a.updatedAt ?? a.createdAt)?.getTime() ?? 0;
        const tb = (b.updatedAt ?? b.createdAt)?.getTime() ?? 0;
        return tb - ta;
      }));
    });

    return byCycle;
  }, [filtered]);

  // Summary stats
  const stats = useMemo(() => ({
    total:      sessions.length,
    complete:   sessions.filter(s => ['RUN_COMPLETE', 'PUBLISHED'].includes(s.state)).length,
    published:  sessions.filter(s => s.state === 'PUBLISHED').length,
    inProgress: sessions.filter(s => !['DRAFT', 'RUN_COMPLETE', 'PUBLISHED', 'ARCHIVED'].includes(s.state)).length,
  }), [sessions]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Trophy className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Session Results"
        subtitle="Ranking results from prioritization sessions, organised by cycle and Track. Ranking informs priority; it does not establish it."
      />

      {/* Agent bar — agents relevant to session ranking results */}
      <AgentBar agents={pageAgents} variant="inline" />

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total',       value: stats.total,      icon: Target,       color: 'text-[var(--text-sec)]',   bg: 'bg-[var(--hover-tint)]'   },
          { label: 'In Progress', value: stats.inProgress, icon: Clock,        color: 'text-[var(--mustard)]',  bg: 'bg-[var(--mustard-dim)]'  },
          { label: 'Complete',    value: stats.complete,   icon: CheckCircle2, color: 'text-[var(--text-ter)]',   bg: 'bg-[var(--slate-dim)]'   },
          { label: 'Published',   value: stats.published,  icon: Trophy,       color: 'text-[var(--green)]',  bg: 'bg-[var(--green-dim)]'  },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="flex items-center gap-3 border border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-3">
            <div className={`p-2  ${bg}`}>
              <Icon className={`h-4 w-4 ${color}`} />
            </div>
            <div>
              <p className="text-xl font-bold text-[var(--text-pri)] leading-none">{value}</p>
              <p className="text-xs text-[var(--text-ter)] mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)] pointer-events-none" />
          <input
            type="text"
            placeholder="Search sessions or cycles…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-8 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1 p-1 bg-[var(--hover-tint)]">
          {[
            { value: 'all',         label: 'All'         },
            { value: 'complete',    label: 'Complete'    },
            { value: 'in-progress', label: 'In Progress' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setStateFilter(opt.value as typeof stateFilter)}
              className={`px-3 py-1.5 text-xs font-medium  transition-colors ${
                stateFilter === opt.value
                  ? 'bg-[var(--bg-raised)] text-[var(--text-pri)] shadow-sm'
                  : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grouped content */}
      {grouped.size === 0 ? (
        <div className="border border-[var(--rule)] bg-[var(--surface)] px-6 py-12 text-center">
          <Filter className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-3" />
          <p className="text-sm font-medium text-[var(--text-ter)]">
            {search || stateFilter !== 'all' ? 'No sessions match your filters.' : 'No sessions yet.'}
          </p>
          {(search || stateFilter !== 'all') && (
            <button
              onClick={() => { setSearch(''); setStateFilter('all'); }}
              className="mt-2 text-xs text-primary-600 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {[...grouped.entries()].map(([cycleId, byTrack]) => {
            const cycleName = cycleId === '__no_cycle__' ? 'No Cycle' : (cycleMap.get(cycleId) ?? 'Unknown Cycle');

            const sessionsByTrack = new Map<string, { trackName: string; sessions: PrioritizationSession[] }>();
            byTrack.forEach((sessions, trackId) => {
              const trackName = trackId === '__no_track__'
                ? 'No Track'
                : (trackMap.get(trackId) ?? 'Unknown Track');
              sessionsByTrack.set(trackId, { trackName, sessions });
            });

            return (
              <CycleGroup
                key={cycleId}
                cycleName={cycleName}
                sessionsByTrack={sessionsByTrack}
                itemsMap={itemsMap}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
